import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import type { Role, HalfDayMethod } from '@/types/Role';
import { COMMON_ROLES } from '@/constants/roles';
import {
  getHalfDayDefaults,
  isPercentageMethod,
  formatCalculatedHalfDayRate,
} from '@/utils/calculateHalfDayRate';
import { formatCurrency } from '@/utils/formatters';
import { getDuplicateRoleError } from '@/utils/roleValidation';

interface UseRoleFormOptions {
  roles: Role[];
  isEdit: boolean;
  originalRole?: Role | null;
  lastSavedRole?: Role | null;
}

export function useRoleForm({ roles, isEdit, originalRole, lastSavedRole }: UseRoleFormOptions) {
  // Initialize state from originalRole in edit mode so the form renders at
  // its correct height on mount — prevents Framer Motion height:auto snap
  // (FM measures target height on first render, before useEffect runs)
  const init = isEdit && originalRole ? originalRole : null;
  const [name, setName] = useState(init?.name ?? '');
  const [description, setDescription] = useState(init?.description ?? '');
  const [billingStructure, setBillingStructure] = useState<'day_rate' | 'hourly' | null>(init?.billingStructure ?? null);
  const [rate, setRate] = useState<number | null>(init?.rate ?? null);
  const [allowHalfDay, setAllowHalfDay] = useState(init?.allowHalfDay ?? false);
  const [halfDayMethod, setHalfDayMethod] = useState<HalfDayMethod | null>(init?.halfDayMethod ?? null);
  const [halfDayMultiplier, setHalfDayMultiplier] = useState<number | null>(init?.halfDayMultiplier ?? null);
  const [halfDayFixedRate, setHalfDayFixedRate] = useState<number | null>(init?.halfDayFixedRate ?? null);
  const [halfDayFixedDiscount, setHalfDayFixedDiscount] = useState<number | null>(init?.halfDayFixedDiscount ?? null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [prefillRoleName, setPrefillRoleName] = useState<string | null>(null);
  const hasOfferedPrefillRef = useRef(isEdit);

  const nameInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const descRef = useRef<HTMLTextAreaElement>(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    if (!showSuggestions) return;
    const handleClick = (e: MouseEvent) => {
      if (
        suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node) &&
        nameInputRef.current && !nameInputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showSuggestions]);

  // Filter suggestions based on name input, marking already-used roles
  const existingRoleNames = useMemo(
    () => new Set(roles.map((r) => r.name.trim().toLowerCase())),
    [roles]
  );

  const filteredSuggestions = useMemo(() => {
    const lower = name.trim().toLowerCase();
    const base = !lower
      ? COMMON_ROLES.slice(0, 5)
      : COMMON_ROLES.filter(
          (r) => r.name.toLowerCase().includes(lower) || r.description.toLowerCase().includes(lower)
        ).slice(0, 5);
    return base.map((r) => ({
      ...r,
      isUsed: existingRoleNames.has(r.name.trim().toLowerCase()),
    }));
  }, [name, existingRoleNames]);

  const selectSuggestion = (suggestion: { name: string; description: string }) => {
    setName(suggestion.name);
    setDescription(suggestion.description);
    setShowSuggestions(false);
  };

  // Computed half-day preview (excludes 'fixed' — that's the literal value)
  const calculatedHalfDayRate = useMemo(
    () => formatCalculatedHalfDayRate(rate, halfDayMethod, halfDayMultiplier, halfDayFixedDiscount),
    [rate, halfDayMethod, halfDayMultiplier, halfDayFixedDiscount],
  );

  // Half-day rate for header badge — covers all methods including 'fixed'
  const headerHalfDayRate = useMemo(() => {
    if (!allowHalfDay || !rate || billingStructure !== 'day_rate') return null;
    if (halfDayMethod === 'fixed' && halfDayFixedRate)
      return formatCurrency(halfDayFixedRate, { showCents: false });
    return calculatedHalfDayRate;
  }, [allowHalfDay, rate, billingStructure, halfDayMethod, halfDayFixedRate, calculatedHalfDayRate]);

  const clearError = (field: string) => {
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  /** Apply half-day defaults from existing roles if no method is set yet */
  const ensureHalfDayDefaults = () => {
    if (!halfDayMethod) {
      const defaults = getHalfDayDefaults(roles);
      setHalfDayMethod(defaults.halfDayMethod);
      setHalfDayMultiplier(defaults.halfDayMultiplier);
    }
  };

  const computeErrors = (): Record<string, string> => {
    const newErrors: Record<string, string> = {};
    const trimmedName = name.trim();

    if (!trimmedName) {
      newErrors.name = 'Enter a role name to continue.';
    } else {
      const existing = roles.find((r) => {
        if (isEdit && originalRole && r.id === originalRole.id) return false;
        return r.name.trim().toLowerCase() === trimmedName.toLowerCase();
      });
      if (existing) {
        newErrors.name = getDuplicateRoleError(existing);
      }
    }

    if (!billingStructure) {
      newErrors.billingStructure = 'Choose a billing structure to continue.';
    }

    if (!rate || rate <= 0) {
      newErrors.rate = 'Set a rate to continue.';
    }

    if (billingStructure === 'day_rate' && allowHalfDay && halfDayMethod) {
      if (halfDayMethod === 'fixed' && (!halfDayFixedRate || halfDayFixedRate <= 0)) {
        newErrors.halfDayFixedRate = 'Set a half-day rate to continue.';
      }
      if (halfDayMethod === 'multiplier' && (!halfDayMultiplier || halfDayMultiplier <= 0)) {
        newErrors.halfDayMultiplier = 'Set a rate percentage to continue.';
      }
      if (halfDayMethod === 'pct_discount' && (!halfDayMultiplier || halfDayMultiplier <= 0)) {
        newErrors.halfDayMultiplier = 'Set a discount percentage to continue.';
      }
      if (halfDayMethod === 'fixed_discount') {
        if (!halfDayFixedDiscount || halfDayFixedDiscount <= 0) {
          newErrors.halfDayFixedDiscount = 'Set a discount amount to continue.';
        } else if (rate && halfDayFixedDiscount >= rate) {
          newErrors.halfDayFixedDiscount = 'Discount must be less than the day rate.';
        }
      }
      if (halfDayMethod === 'fixed' && rate && halfDayFixedRate && halfDayFixedRate >= rate) {
        newErrors.halfDayFixedRate = 'Half-day rate must be less than the full day rate.';
      }
    }

    return newErrors;
  };

  const validate = (): boolean => {
    const newErrors = computeErrors();
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Live validation for footer hints
  const { liveHint, isFormValid } = useMemo(() => {
    const errs = computeErrors();
    const keys = Object.keys(errs);
    if (keys.length === 0) return { liveHint: null, isFormValid: true };
    return { liveHint: errs[keys[0]], isFormValid: false };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, billingStructure, rate, allowHalfDay, halfDayMethod, halfDayMultiplier, halfDayFixedRate, halfDayFixedDiscount, roles, isEdit, originalRole]);

  const buildRole = (): Role => {
    const isDayRateHalfDay = billingStructure === 'day_rate' && allowHalfDay;
    return {
      id: isEdit && originalRole?.id ? originalRole.id : undefined,
      name: name.trim(),
      rate: rate!,
      billingStructure: billingStructure!,
      allowHalfDay: billingStructure === 'day_rate' ? allowHalfDay : false,
      description: description.trim() || null,
      halfDayMethod: isDayRateHalfDay ? halfDayMethod : null,
      halfDayMultiplier: isDayRateHalfDay && isPercentageMethod(halfDayMethod) ? halfDayMultiplier : null,
      halfDayFixedRate: isDayRateHalfDay && halfDayMethod === 'fixed' ? halfDayFixedRate : null,
      halfDayFixedDiscount: isDayRateHalfDay && halfDayMethod === 'fixed_discount' ? halfDayFixedDiscount : null,
    };
  };

  const populateFromRole = (role: Role) => {
    setName(role.name);
    setDescription(role.description || '');
    setBillingStructure(role.billingStructure);
    setRate(role.rate);
    setAllowHalfDay(role.allowHalfDay);
    setHalfDayMethod(role.halfDayMethod);
    setHalfDayMultiplier(role.halfDayMultiplier);
    setHalfDayFixedRate(role.halfDayFixedRate);
    setHalfDayFixedDiscount(role.halfDayFixedDiscount);
    setPrefillRoleName(null);
    hasOfferedPrefillRef.current = true; // editing existing role — never prefill
    setErrors({});
    setShowSuggestions(false);
  };

  // Pre-fill rate when user selects a billing structure matching the last saved role
  // Only offered once per form session, and never after the user has touched the rate
  const handleBillingStructureChange = (value: 'day_rate' | 'hourly' | null) => {
    setBillingStructure(value);
    if (hasOfferedPrefillRef.current) return;
    const prefillRole = lastSavedRole ?? (roles.length > 0 ? roles[roles.length - 1] : null);
    if (value && prefillRole?.rate != null && prefillRole.billingStructure === value && rate == null) {
      hasOfferedPrefillRef.current = true;
      setRate(prefillRole.rate);
      setPrefillRoleName(prefillRole.name || null);
    }
  };

  const resetToDefaults = () => {
    setName('');
    setDescription('');
    setBillingStructure(null);
    setRate(null);
    setPrefillRoleName(null);
    hasOfferedPrefillRef.current = false;
    setAllowHalfDay(false);
    const defaults = getHalfDayDefaults(roles);
    setHalfDayMethod(defaults.halfDayMethod);
    setHalfDayMultiplier(defaults.halfDayMultiplier);
    setHalfDayFixedRate(defaults.halfDayFixedRate);
    setHalfDayFixedDiscount(defaults.halfDayFixedDiscount);
    setErrors({});
    setShowSuggestions(false);
  };

  return {
    // State
    name, setName,
    description, setDescription,
    billingStructure, setBillingStructure: handleBillingStructureChange,
    rate, setRate,
    allowHalfDay, setAllowHalfDay,
    halfDayMethod, setHalfDayMethod,
    halfDayMultiplier, setHalfDayMultiplier,
    halfDayFixedRate, setHalfDayFixedRate,
    halfDayFixedDiscount, setHalfDayFixedDiscount,
    errors, setErrors,
    showSuggestions, setShowSuggestions,
    prefillRoleName, clearPrefill: useCallback(() => setPrefillRoleName(null), []),

    // Refs
    nameInputRef, suggestionsRef, descRef,

    // Computed
    filteredSuggestions,
    calculatedHalfDayRate,
    headerHalfDayRate,
    liveHint,
    isFormValid,

    // Actions
    selectSuggestion,
    clearError,
    ensureHalfDayDefaults,
    validate,
    buildRole,
    populateFromRole,
    resetToDefaults,
  };
}
